import React, { Suspense, useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Environment, Grid } from '@react-three/drei';
import type { Vector3 as _Vector3 } from 'three';
import { useShotVisualizationStore } from '../stores/shotVisualizationStore';
import { CricketField } from './CricketField';
import { TrajectoryPath } from './TrajectoryPath';
import { Ball } from './Ball';
import { Bat } from './Bat';
import { PhysicsAnnotations } from './PhysicsAnnotations';

interface Scene3DProps {
  className?: string;
}

export const Scene3D: React.FC<Scene3DProps> = ({ className = '' }) => {
  const cameraRef = useRef<any>();
  const {
    shotComparison,
    config,
    animation,
    camera,
    setCameraView
  } = useShotVisualizationStore();

  // Update camera when settings change
  useEffect(() => {
    if (cameraRef.current && config.cameraView !== 'free') {
      cameraRef.current.position.copy(camera.position);
      cameraRef.current.lookAt(camera.target);
    }
  }, [camera, config.cameraView]);

  return (
    <div className={`relative w-full h-full bg-gradient-to-b from-sky-200 to-green-100 ${className}`}>
      <Canvas
        shadows
        camera={{ 
          position: [camera.position.x, camera.position.y, camera.position.z],
          fov: camera.fov,
          near: camera.near,
          far: camera.far
        }}
        className="w-full h-full"
      >
        <Suspense fallback={null}>
          {/* Camera */}
          <PerspectiveCamera
            ref={cameraRef}
            makeDefault
            position={[camera.position.x, camera.position.y, camera.position.z]}
            fov={camera.fov}
            near={camera.near}
            far={camera.far}
          />

          {/* Controls */}
          {config.cameraView === 'free' && (
            <OrbitControls
              target={[camera.target.x, camera.target.y, camera.target.z]}
              enablePan={true}
              enableZoom={true}
              enableRotate={true}
              maxPolarAngle={Math.PI / 2}
              minDistance={5}
              maxDistance={100}
            />
          )}

          {/* Lighting */}
          <ambientLight intensity={0.4} />
          <directionalLight
            position={[10, 20, 5]}
            intensity={1}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
            shadow-camera-far={50}
            shadow-camera-left={-10}
            shadow-camera-right={10}
            shadow-camera-top={10}
            shadow-camera-bottom={-10}
          />
          <pointLight position={[-10, 10, -10]} intensity={0.3} />

          {/* Environment */}
          <Environment preset="sunset" />
          
          {/* Ground grid */}
          <Grid
            position={[0, 0, 0]}
            args={[100, 100]}
            cellSize={5}
            cellThickness={0.5}
            cellColor="#6b7280"
            sectionSize={10}
            sectionThickness={1.5}
            sectionColor="#374151"
            fadeDistance={50}
            fadeStrength={1}
            followCamera={false}
            infiniteGrid={true}
          />

          {/* Cricket Field */}
          {config.showField && <CricketField />}

          {/* Trajectory Paths */}
          {shotComparison && (
            <>
              {config.showActualTrajectory && (
                <TrajectoryPath
                  trajectory={shotComparison.actualTrajectory}
                  color="#ef4444"
                  opacity={0.9}
                  animated={animation.isPlaying}
                  animationTime={animation.currentTime}
                  lineWidth={8}
                />
              )}
              {config.showOptimalTrajectory && (
                <TrajectoryPath
                  trajectory={shotComparison.optimalTrajectory}
                  color="#22c55e"
                  opacity={0.8}
                  animated={false}
                  lineWidth={6}
                  dashed={true}
                />
              )}
            </>
          )}

          {/* Ball */}
          {config.showBall && shotComparison && (
            <Ball
              trajectory={shotComparison.actualTrajectory}
              animation={animation}
              trailLength={config.ballTrailLength}
            />
          )}

          {/* Bat */}
          {config.showBat && shotComparison && (
            <Bat
              shot={shotComparison.actual}
              animation={animation}
              showSweetSpot={true}
            />
          )}

          {/* Physics Annotations */}
          {config.showMetrics && shotComparison && (
            <PhysicsAnnotations
              shotComparison={shotComparison}
              animation={animation}
            />
          )}
        </Suspense>
      </Canvas>

      {/* Camera Controls */}
      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
        <div className="text-sm font-medium text-gray-700 mb-2">Camera View</div>
        <div className="flex flex-col gap-1">
          {['side', 'behind', 'above', 'free'].map((view) => (
            <button
              key={view}
              onClick={() => setCameraView(view)}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                config.cameraView === view
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {view.charAt(0).toUpperCase() + view.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Animation Phase Indicator */}
      {animation.isPlaying && (
        <div className="absolute bottom-4 left-4 bg-black/80 text-white px-3 py-2 rounded-lg">
          <div className="text-sm font-medium">
            {animation.currentPhase.charAt(0).toUpperCase() + animation.currentPhase.slice(1)}
          </div>
          <div className="text-xs opacity-75">
            {(animation.currentTime / 1000).toFixed(1)}s / {(animation.totalDuration / 1000).toFixed(1)}s
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {!shotComparison && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-lg p-6 text-center">
            <div className="text-gray-600">Load shot data to begin visualization</div>
          </div>
        </div>
      )}
    </div>
  );
};